import React, {FC, useEffect, useState} from 'react';

import {buildContactsManagerAccountImportPayload} from '../../../../../libs/contacts-manager/src/lib/ui/web/helpers/trackingHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInterval, usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AccountsJobModel, JobStatusesEnum} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerProgress} from '../../../../../libs/shared/ui/src/components/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerSecondaryText} from '../../../../../libs/shared/ui/src/components/banners/bannerSecondaryText';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Constants.
 */

// success banner should disappear after 8 seconds
const bannerTimeLimit = 8000; // ms

/*
 * Props.
 */

interface AccountsCsvImportBannerProps {
  job?: AccountsJobModel;
  /** A fraction indicating the progress of the import. */
  progress: number;
  isErrorBanner?: boolean;
  contactsManagerTableForceRefresh: () => void;
  onDismissClick: () => void;
}

/*
 * Component.
 */

export const AccountsCSVImportBanner: FC<AccountsCsvImportBannerProps> = (props) => {
  const {job, progress, isErrorBanner, contactsManagerTableForceRefresh, onDismissClick} = props;

  const [setInterval] = useInterval();
  const [isSuccessBannerExpired, setSuccessBannerExpired] = useState(false);
  const isImporting = progress < 1;
  const previousStatus = usePrevious(job?.status);
  const isJobDone = job?.status === JobStatusesEnum.DONE;

  useEffect(() => {
    // dispatch an event to refresh the accounts table the job just completed
    if (isJobDone && previousStatus !== JobStatusesEnum.DONE) {
      contactsManagerTableForceRefresh();
      trackInterfaceEvent(
        InterfaceEventsEnum.CONTACTS_MANAGER_ACCOUNT_IMPORT_SUCCESS,
        buildContactsManagerAccountImportPayload(job?.stepCount || 0),
      );
      // if the job just finished set interval to expire the success banner
      setInterval(() => {
        setSuccessBannerExpired(true);
      }, bannerTimeLimit);
    }
  }, [
    isJobDone,
    previousStatus,
    job?.stepCount,
    setSuccessBannerExpired,
    setInterval,
    contactsManagerTableForceRefresh,
  ]);

  if (job?.status === JobStatusesEnum.FAILED || isErrorBanner) {
    return renderErrorBanner(onDismissClick);
  }

  // Otherwise, check if the import is still in progress or if it has just completed.
  return isImporting && !isJobDone
    ? renderProgressBanner(progress)
    : maybeRenderCompletedBanner(isSuccessBannerExpired, onDismissClick);
};

function renderProgressBanner(progress: number) {
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>
        <Trans id="banner-accounts-csv-import-importing">Importing your accounts...</Trans>
      </BannerText>
      <BannerSecondaryText>
        <Trans id="banner-accounts-csv-import-progress">{Math.floor(100 * progress)}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
}

function maybeRenderCompletedBanner(isSuccessBannerExpired: boolean, onDismissClick: () => void) {
  if (isSuccessBannerExpired) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerText>
            <Trans id="banner-accounts-csv-import-successful">Accounts imported successfully</Trans>
          </BannerText>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}

function renderErrorBanner(onDismissClick: () => void) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.MULTILINE}
          color={theme.palette.red.shade40}
          priority={BannerPrioritiesEnum.ERROR}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="warningFilledSmall" />
          <BannerContent>
            <BannerContentTitle>
              <Trans id="banner-accounts-csv-import-failed">CSV import failed</Trans>
            </BannerContentTitle>
            <Trans id="banner-accounts-csv-import-failed-try-again-message">Please try again.</Trans>
          </BannerContent>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}
